// ============================================================================
// Frontend API Client with Auth Interceptor and Error Handling
// ============================================================================

import {
  ApiResponse,
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  StartExamRequest,
  SubmitAnswerRequest,
  CompleteExamResponse,
  TrackSummary,
  Question,
  User,
  Subscription,
} from '../../shared/types';
import { authClient } from './auth-client';

const TOKEN_STORAGE_KEY = 'ramsay_auth_token';
const USER_STORAGE_KEY = 'ramsay_auth_user';

export const authStorage = {
  getToken: (): string | null => localStorage.getItem(TOKEN_STORAGE_KEY),
  setToken: (token: string) => localStorage.setItem(TOKEN_STORAGE_KEY, token),
  clearToken: () => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
  },
  getUser: (): User | null => {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  },
  setUser: (user: User) => localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user)),
};

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = authStorage.getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(endpoint, {
    credentials: 'include',
    ...options,
    headers,
  });

  const data = (await response.json().catch(() => ({
    success: false,
    error: { message: 'Invalid response from server' },
  }))) as ApiResponse<T>;

  if (!response.ok || !data.success) {
    const message = data.error?.message || `Request failed with status ${response.status}`;
    if (response.status === 401) {
      // Auto clear invalid token
      authStorage.clearToken();
    }
    throw new Error(message);
  }

  return data.data as T;
}

export const api = {
  // Auth
  auth: {
    register: async (payload: RegisterRequest): Promise<AuthResponse & { requiresEmailVerification?: boolean }> => {
      const data = await request<AuthResponse & { requiresEmailVerification?: boolean }>('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (data.token) {
        authStorage.setToken(data.token);
      }
      if (data.user && data.user.emailVerified) {
        authStorage.setUser(data.user);
      }
      return data;
    },
    login: async (payload: LoginRequest): Promise<AuthResponse> => {
      const data = await request<AuthResponse>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      authStorage.setToken(data.token);
      authStorage.setUser(data.user);
      return data;
    },
    google: {
      getConfig: async (): Promise<{ configured: boolean; clientId: string | null }> => {
        return request<{ configured: boolean; clientId: string | null }>('/api/auth/google/config');
      },
      signIn: async (callbackURL = window.location.origin + '/#/') => {
        const res = await authClient.signIn.social({
          provider: 'google',
          callbackURL,
        });
        return res;
      },
    },
    apple: {
      getConfig: async (): Promise<{ configured: boolean; clientId: string | null; appBundleIdentifier?: string; keyId?: string | null }> => {
        return request<{ configured: boolean; clientId: string | null; appBundleIdentifier?: string; keyId?: string | null }>('/api/auth/apple/config');
      },
      signIn: async (callbackURL = window.location.origin + '/#/') => {
        const res = await authClient.signIn.social({
          provider: 'apple',
          callbackURL,
          errorCallbackURL: window.location.origin + '/#/?error=apple_auth_failed',
        });
        return res;
      },
    },
    me: async (): Promise<{ user: User; subscription: Subscription }> => {
      return request<{ user: User; subscription: Subscription }>('/api/auth/me');
    },
    logout: async () => {
      try {
        await authClient.signOut();
      } catch (err) {
        console.warn('Sign out failed:', err);
      }
      authStorage.clearToken();
    },
  },

  // Billing & Stripe Subscriptions
  billing: {
    getConfig: async (): Promise<{
      publishableKey: string;
      priceId: string;
      productId: string;
      planName: string;
      trialDays: number;
      monthlyPrice: number;
      currency: string;
    }> => {
      return request('/api/billing/config');
    },
    createCheckoutSession: async (): Promise<{ url: string; sessionId: string }> => {
      return request('/api/billing/create-checkout-session', {
        method: 'POST',
      });
    },
    createPortalSession: async (): Promise<{ url: string }> => {
      return request('/api/billing/create-portal-session', {
        method: 'POST',
      });
    },
  },

  // Tracks & Categories
  tracks: {
    getAll: async (): Promise<{ tracks: TrackSummary[]; totalAvailableQuestions: number }> => {
      return request<{ tracks: TrackSummary[]; totalAvailableQuestions: number }>('/api/tracks');
    },
    getOne: async (track: string): Promise<TrackSummary> => {
      return request<TrackSummary>(`/api/tracks/${track}`);
    },
  },

  // Questions
  questions: {
    list: async (params?: { track?: string; categoryId?: string; difficulty?: string; limit?: number }): Promise<{ questions: Question[]; count: number }> => {
      const searchParams = new URLSearchParams();
      if (params?.track) searchParams.set('track', params.track);
      if (params?.categoryId) searchParams.set('categoryId', params.categoryId);
      if (params?.difficulty) searchParams.set('difficulty', params.difficulty);
      if (params?.limit) searchParams.set('limit', params.limit.toString());

      return request<{ questions: Question[]; count: number }>(`/api/questions?${searchParams.toString()}`);
    },
  },

  // Exams
  exams: {
    start: async (payload: StartExamRequest): Promise<{
      sessionId: string;
      track: string;
      mode: string;
      timeLimitSeconds: number;
      totalQuestions: number;
      startedAt: number;
      questions: Question[];
    }> => {
      return request('/api/exams/start', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
    submitAnswer: async (sessionId: string, payload: SubmitAnswerRequest): Promise<{
      questionId: string;
      selectedIndex: number;
      isCorrect: boolean;
      correctIndex?: number;
      explanation?: string;
    }> => {
      return request(`/api/exams/${sessionId}/answer`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
    complete: async (sessionId: string): Promise<CompleteExamResponse> => {
      return request(`/api/exams/${sessionId}/complete`, {
        method: 'POST',
      });
    },
    getHistory: async (): Promise<{ sessions: any[] }> => {
      return request('/api/exams/history');
    },
    getById: async (sessionId: string): Promise<{ session: any; answers: any[] }> => {
      return request(`/api/exams/${sessionId}`);
    },
  },

  // Health
  health: async () => {
    const res = await fetch('/api/health');
    return res.json();
  },
};
